import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, DollarSign, Check } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import { getCachedBarberServices, setCachedBarberServices } from '../../../utils/barberServicesCache';
import '../ModuleServices/ModuleServices.css';

const BarberServices = ({ barber, onSelectService, onBack }) => {
    const barberId = barber?.id;
    const cachedServices = barberId ? getCachedBarberServices(barberId) : null;

    const [services, setServices] = useState(() => cachedServices || []);
    const [loading, setLoading] = useState(() => !cachedServices);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!barberId) return;

        const cached = getCachedBarberServices(barberId);
        if (cached) {
            setServices(cached);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;

        const fetchServices = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${API_BASE_URL}/barber/barbers/${barberId}/services`);

                if (!response.ok) {
                    throw new Error('Error al cargar los servicios');
                }

                const data = await response.json();

                if (cancelled) return;

                setCachedBarberServices(barberId, data);
                setServices(data);
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching barber services:', err);
                setError(err.message);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchServices();

        return () => {
            cancelled = true;
        };
    }, [barberId]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    const barberName = barber?.nombre || barber?.name || 'Barbero';

    if (loading) {
        return (
            <div className="module-services-loading">
                <div className="spinner"></div>
                <p>Cargando servicios...</p>
            </div>
        );
    }

    return (
        <div className="module-services">
            <div className="module-services-header">
                <button type="button" className="back-button" onClick={onBack}>
                    <ArrowLeft size={20} />
                    <span>Volver a barberos</span>
                </button>
                <div className="module-services-title">
                    <h2>Servicios de {barberName}</h2>
                    <p className="module-description">Elige el servicio que deseas reservar</p>
                </div>
            </div>

            {error ? (
                <div className="module-services-error">
                    <p>Error al cargar los servicios: {error}</p>
                </div>
            ) : services.length === 0 ? (
                <div className="module-services-empty">
                    <p>Este barbero no tiene servicios disponibles en este momento</p>
                </div>
            ) : (
                <div className="services-grid">
                    {services.map((service, index) => (
                        <motion.div
                            key={service.id}
                            className="service-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            onClick={() => onSelectService(service)}
                            style={{
                                '--module-color': '#ef4444',
                                cursor: 'pointer'
                            }}
                        >
                            <div className="service-card-header">
                                <h3 className="service-name">{service.name}</h3>
                            </div>

                            <div className="service-card-body">
                                <div className="service-info">
                                    <div className="service-price">
                                        <DollarSign size={20} />
                                        <span>{formatPrice(service.price)}</span>
                                    </div>
                                    <div className="service-duration">
                                        <Clock size={16} />
                                        <span>{service.duration_minutes} min</span>
                                    </div>
                                </div>
                            </div>

                            <div className="service-card-footer">
                                <span>Seleccionar servicio</span>
                                <Check size={18} />
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BarberServices;
